import React, { useState, useEffect } from 'react';
import { User, BoardMember, assignmentApi, boardMemberApi, getCardApiErrorMessage } from '../services/api';
import { Plus, Xmark } from 'iconoir-react';

interface CardAssignmentsProps {
  cardId: number;
  boardId: number;
  canEdit?: boolean;
  onAssignmentsChange?: () => void;
  remoteUsers?: User[];
}

export const CardAssignments: React.FC<CardAssignmentsProps> = ({ cardId, boardId, canEdit = false, onAssignmentsChange, remoteUsers }) => {
  const [assignedUsers, setAssignedUsers] = useState<User[]>([]);
  const [boardMembers, setBoardMembers] = useState<BoardMember[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadAssignedUsers();
    loadBoardMembers();
  }, [cardId, boardId]);

  useEffect(() => {
    if (remoteUsers === undefined) return;
    setAssignedUsers(remoteUsers);
  }, [remoteUsers]);

  const loadAssignedUsers = async () => {
    try {
      const response = await assignmentApi.getAll(cardId);
      setAssignedUsers(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des membres affectés', error);
    }
  };

  const loadBoardMembers = async () => {
    try {
      const response = await boardMemberApi.getAll(boardId);
      setBoardMembers(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des membres du tableau', error);
    }
  };

  const handleAssign = async (userId: number) => {
    setIsLoading(true);
    try {
      await assignmentApi.assign(cardId, userId);
      await loadAssignedUsers();
      setIsAdding(false);
      onAssignmentsChange?.();
    } catch (error: any) {
      console.error('Erreur lors de l\'affectation du membre', error);
      if (error.response?.status === 403) {
        window.alert(getCardApiErrorMessage(error, 'Vous n\'avez pas la permission d\'affecter des membres à cette carte.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnassign = async (userId: number) => {
    setIsLoading(true);
    try {
      await assignmentApi.unassign(cardId, userId);
      await loadAssignedUsers();
      onAssignmentsChange?.();
    } catch (error: any) {
      console.error('Erreur lors du retrait du membre', error);
      if (error.response?.status === 403) {
        window.alert(getCardApiErrorMessage(error, 'Vous n\'avez pas la permission de retirer des membres de cette carte.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const availableMembers = boardMembers.filter(
    member => !assignedUsers.some(assigned => assigned.id === member.id) && 
    (member.role === 'owner' || member.role === 'admin' || member.role === 'editor' || member.role === 'visitor')
  );

  return (
    <div className="card-assignments">
      <div className="card-assignments-header">
        <h4>Membres</h4>
        {canEdit && (
          <button
            className="btn-icon"
            onClick={() => setIsAdding(!isAdding)}
            title="Affecter un membre"
          >
            <Plus width={18} height={18} strokeWidth={2} />
          </button>
        )}
      </div>

      {/* Liste des membres affectés */}
      <div className="assigned-users-list">
        {assignedUsers.length > 0 ? (
          assignedUsers.map(user => (
            <div key={user.id} className="assigned-user-item">
              <div className="assigned-user-info">
                <div className="assigned-user-avatar">
                  {user.avatar_url ? (
                    <img src={user.avatar_url} alt={user.name} className="assigned-user-avatar-image" />
                  ) : (
                    user.name.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="assigned-user-details">
                  <div className="assigned-user-name">{user.name}</div>
                  <div className="assigned-user-email">{user.email}</div>
                </div>
              </div>
              {canEdit && (
                <button
                  className="btn-icon-small"
                  onClick={() => handleUnassign(user.id)}
                  title="Retirer"
                  disabled={isLoading}
                >
                  <Xmark width={16} height={16} strokeWidth={2} />
                </button>
              )}
            </div>
          ))
        ) : (
          <div className="no-assignments">Aucun membre affecté</div>
        )}
      </div>

      {/* Formulaire d'ajout */}
      {canEdit && isAdding && availableMembers.length > 0 && (
        <div className="assignment-add-form">
          <div className="available-members-list">
            {availableMembers.map(member => (
              <div
                key={member.id}
                className="available-member-item"
                onClick={() => handleAssign(member.id)}
              >
                <div className="assigned-user-avatar">
                  {member.avatar_url ? (
                    <img src={member.avatar_url} alt={member.name} className="assigned-user-avatar-image" />
                  ) : (
                    member.name.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="assigned-user-details">
                  <div className="assigned-user-name">{member.name}</div>
                  <div className="assigned-user-email">{member.email}</div>
                </div>
              </div>
            ))}
          </div>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => setIsAdding(false)}
            disabled={isLoading}
          >
            Annuler
          </button>
        </div>
      )}

      {canEdit && isAdding && availableMembers.length === 0 && (
        <div className="no-available-members">
          Tous les membres du tableau sont déjà affectés
        </div>
      )}
    </div>
  );
};
